import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { rotuloStatus, rotuloTipo, corStatus } from '../lib/rotulos';

interface OS {
  id: number;
  codigo: string;
  tipo_os: string;
  status: string;
  unidade_nome: string | null;
  local_nome: string | null;
  cliente_nome?: string | null;
  veiculo_placa?: string | null;
  veiculo_marca?: string | null;
  veiculo_modelo?: string | null;
  veiculo_ano?: number | null;
  tecnico_id: number | null;
  tecnico_nome: string | null;
  data_agendada: string | null;
  hora_agendada: string | null;
  inicio_atendimento?: string | null;
}

interface Tecnico {
  id: number;
  nome: string;
}

type ProgramacaoModo = 'data' | 'recorrente';
type ProgramacaoFrequencia = 'semanal' | 'mensal' | 'bimestral' | 'trimestral' | 'anual';

interface ProgramacaoResposta {
  total: number;
  os: Array<{ id: number; codigo: string; data_agendada: string; hora_agendada: string | null }>;
}

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const DIAS_COMPLETOS = ['Domingo', 'Segunda-feira', 'Terca-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sabado'];
const FREQUENCIAS_PROGRAMACAO: Array<{ valor: ProgramacaoFrequencia; rotulo: string }> = [
  { valor: 'semanal', rotulo: 'Semanal' },
  { valor: 'mensal', rotulo: 'Mensal' },
  { valor: 'bimestral', rotulo: 'Bimestral' },
  { valor: 'trimestral', rotulo: 'Trimestral' },
  { valor: 'anual', rotulo: 'Anual' },
];
const PERIODOS_PROGRAMACAO = [1, 2, 3, 6, 12, 18, 24, 36, 48, 60];
const STATUS = ['aberto', 'em_andamento', 'interrompido', 'aguardando_aprovacao', 'concluido', 'cancelado'];

function isoDia(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nomeMes(d: Date): string {
  return d.toLocaleDateString('pt-BR', { month: 'long' });
}

function diaSemanaIso(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return '';
  return String(d.getDay());
}

function dataInicioExecucaoIso(os: OS): string | null {
  const valor = os.inicio_atendimento ? String(os.inicio_atendimento) : '';
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isFinite(data.getTime())) {
    return data.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  }
  const match = valor.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}

function hora(os: OS): string {
  return os.hora_agendada ? String(os.hora_agendada).slice(0, 5) : '--:--';
}

function veiculoLabel(os: OS): string {
  return [os.veiculo_placa, os.veiculo_marca, os.veiculo_modelo, os.veiculo_ano].filter(Boolean).join(' ');
}

function alvoAgenda(os: OS, modoOficina: boolean): string {
  if (modoOficina) return [veiculoLabel(os), os.cliente_nome].filter(Boolean).join(' - ') || '-';
  return [os.unidade_nome, os.local_nome].filter(Boolean).join(' / ') || '-';
}

function servicoAgenda(os: OS, modoOficina: boolean): string {
  return modoOficina ? (os.tipo_os || 'Servico') : rotuloTipo(os.tipo_os);
}

function inicioDoMes(iso: string): Date {
  const [ano, mes] = iso.split('-').map(Number);
  if (!ano || !mes) return new Date();
  return new Date(ano, mes - 1, 1);
}

export default function Agenda() {
  const { user } = useAuth();
  const modoOficina = user?.modo_visualizacao === 'oficina';
  const hojeIso = isoDia(new Date());
  const [lista, setLista] = useState<OS[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [erro, setErro] = useState('');
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [tecnicoId, setTecnicoId] = useState('');
  const [status, setStatus] = useState('');
  const [diaSemana, setDiaSemana] = useState('');
  const [dataSelecionada, setDataSelecionada] = useState(hojeIso);
  const [modalDiaAberto, setModalDiaAberto] = useState(false);
  const [programandoOs, setProgramandoOs] = useState<OS | null>(null);
  const [programacaoModo, setProgramacaoModo] = useState<ProgramacaoModo>('data');
  const [programacaoFrequencia, setProgramacaoFrequencia] = useState<ProgramacaoFrequencia>('semanal');
  const [programacaoPeriodoMeses, setProgramacaoPeriodoMeses] = useState('12');
  const [programacaoData, setProgramacaoData] = useState(hojeIso);
  const [programacaoDiasSemana, setProgramacaoDiasSemana] = useState<number[]>(() => {
    const hoje = Number(diaSemanaIso(hojeIso));
    return Number.isFinite(hoje) ? [hoje] : [1];
  });
  const [programacaoHora, setProgramacaoHora] = useState('');
  const [programacaoMsg, setProgramacaoMsg] = useState('');
  const [programacaoErro, setProgramacaoErro] = useState('');
  const [programacaoSalvando, setProgramacaoSalvando] = useState(false);
  const podeProgramarOs = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeVerSemTecnicoNaAgenda = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');

  const dataAgenda = useCallback((os: OS): string | null => {
    if (os.data_agendada) return os.data_agendada;
    if (podeVerSemTecnicoNaAgenda && !os.tecnico_id) return dataInicioExecucaoIso(os);
    return null;
  }, [podeVerSemTecnicoNaAgenda]);

  async function carregar() {
    setErro('');
    try {
      const [os, tec] = await Promise.all([
        api<OS[]>('/os/os_listar.php'),
        api<Tecnico[]>('/cadastros/tecnicos.php').catch(() => [] as Tecnico[]),
      ]);
      setLista(os);
      setTecnicos(tec);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  useEffect(() => {
    if (!modalDiaAberto) return;
    const fecharComEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fecharModalDia();
    };
    window.addEventListener('keydown', fecharComEsc);
    return () => window.removeEventListener('keydown', fecharComEsc);
  }, [modalDiaAberto]);

  const filtradas = useMemo(() => lista.filter((os) => {
    if (tecnicoId && String(os.tecnico_id || '') !== tecnicoId) return false;
    if (status && os.status !== status) return false;
    if (diaSemana && diaSemanaIso(dataAgenda(os)) !== diaSemana) return false;
    return true;
  }), [lista, tecnicoId, status, diaSemana, dataAgenda]);

  const diasMes = useMemo(() => {
    const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const inicio = new Date(primeiro);
    inicio.setDate(1 - primeiro.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(inicio);
      d.setDate(inicio.getDate() + i);
      return d;
    });
  }, [mes]);

  const osDiaSelecionado = useMemo(() => (
    filtradas
      .filter((os) => dataAgenda(os) === dataSelecionada)
      .sort((a, b) => hora(a).localeCompare(hora(b)))
  ), [filtradas, dataSelecionada, dataAgenda]);

  const tituloDataSelecionada = useMemo(() => {
    const dataBase = dataSelecionada ? new Date(`${dataSelecionada}T00:00:00`) : new Date();
    return dataBase.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  }, [dataSelecionada]);

  function mudarMes(delta: number) {
    setMes((atual) => new Date(atual.getFullYear(), atual.getMonth() + delta, 1));
  }

  function osDoDia(d: Date): OS[] {
    const key = isoDia(d);
    return filtradas
      .filter((os) => dataAgenda(os) === key)
      .sort((a, b) => hora(a).localeCompare(hora(b)));
  }

  function selecionarDia(iso: string) {
    setDataSelecionada(iso);
    setMes(inicioDoMes(iso));
  }

  function abrirDia(iso: string) {
    selecionarDia(iso);
    setProgramandoOs(null);
    setProgramacaoMsg('');
    setProgramacaoErro('');
    setModalDiaAberto(true);
  }

  function fecharModalDia() {
    setModalDiaAberto(false);
    setProgramandoOs(null);
    setProgramacaoMsg('');
    setProgramacaoErro('');
  }

  function selecionarHoje() {
    setDiaSemana('');
    selecionarDia(hojeIso);
  }

  function abrirProgramacao(os: OS) {
    setProgramandoOs(os);
    setProgramacaoModo('data');
    setProgramacaoFrequencia('semanal');
    setProgramacaoPeriodoMeses('12');
    setProgramacaoData(dataSelecionada || hojeIso);
    setProgramacaoDiasSemana(() => {
      const dia = Number(diaSemanaIso(dataSelecionada || hojeIso));
      return Number.isFinite(dia) ? [dia] : [1];
    });
    setProgramacaoHora(os.hora_agendada ? String(os.hora_agendada).slice(0, 5) : '');
    setProgramacaoMsg('');
    setProgramacaoErro('');
  }

  async function salvarProgramacao() {
    if (!programandoOs) return;
    setProgramacaoSalvando(true);
    setProgramacaoErro('');
    setProgramacaoMsg('');
    try {
      const payload: any = {
        ordem_servico_id: programandoOs.id,
        modo: programacaoModo,
        hora_agendada: programacaoHora || null,
      };
      if (programacaoModo === 'data') {
        payload.data_agendada = programacaoData;
      } else {
        payload.data_inicio = programacaoData;
        payload.frequencia = programacaoFrequencia;
        payload.periodo_meses = Number(programacaoPeriodoMeses);
        if (programacaoFrequencia === 'semanal') {
          if (!programacaoDiasSemana.length) {
            setProgramacaoErro('Selecione ao menos um dia da semana.');
            setProgramacaoSalvando(false);
            return;
          }
          payload.dias_semana = programacaoDiasSemana;
        }
      }
      const resp = await api<ProgramacaoResposta>('/os/os_programar.php', { method: 'POST', body: payload });
      setProgramacaoMsg(`${resp.total} OS programada(s) com sucesso.`);
      await carregar();
    } catch (e) {
      setProgramacaoErro(e instanceof Error ? e.message : String(e));
    } finally {
      setProgramacaoSalvando(false);
    }
  }

  const semAgenda = diaSemana ? [] : filtradas.filter((os) => !os.data_agendada);

  return (
    <div className="agenda-page">
      <div className="titulo-pg">
        <h1>{modoOficina ? 'Agenda do tecnico' : 'Agenda'}</h1>
        <button type="button" className="btn sec sm" onClick={carregar}>Atualizar</button>
      </div>

      <div className="card agenda-toolbar">
        <div>
          <label>Tecnico</label>
          <select value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
            <option value="">Todos</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </div>
        <div>
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos</option>
            {STATUS.map((s) => <option key={s} value={s}>{rotuloStatus(s)}</option>)}
          </select>
        </div>
        <div>
          <label>Dia</label>
          <select value={diaSemana} onChange={(e) => setDiaSemana(e.target.value)}>
            <option value="">Todos</option>
            {DIAS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label>Data</label>
          <input className="input" type="date" value={dataSelecionada} onChange={(e) => selecionarDia(e.target.value || hojeIso)} />
        </div>
        <div className="agenda-mes">
          <button type="button" className="btn sec sm" onClick={() => mudarMes(-1)}>Anterior</button>
          <strong>{nomeMes(mes)}</strong>
          <button type="button" className="btn sec sm" onClick={selecionarHoje}>Hoje</button>
          <button type="button" className="btn sec sm" onClick={() => mudarMes(1)}>Proximo</button>
        </div>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}

      <div className="agenda-calendar">
        {DIAS.map((d) => <div key={d} className="agenda-weekday">{d}</div>)}
        {diasMes.map((d) => {
          const diaIso = isoDia(d);
          const fora = d.getMonth() !== mes.getMonth();
          const hoje = diaIso === hojeIso;
          const selecionado = diaIso === dataSelecionada;
          const itens = osDoDia(d);
          return (
            <div
              key={diaIso}
              className={'agenda-day' + (fora ? ' fora' : '') + (hoje ? ' hoje' : '') + (selecionado ? ' selecionado' : '')}
              onClick={() => abrirDia(diaIso)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  abrirDia(diaIso);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="agenda-day-num">{d.getDate()}</div>
              <div className="agenda-items">
                {itens.slice(0, 4).map((os) => (
                  <div key={os.id} className="agenda-os" style={{ borderLeftColor: corStatus(os.status) }}>
                    <b>{hora(os)} {os.codigo}</b>
                    <span>{servicoAgenda(os, modoOficina)} · {os.tecnico_nome || 'sem tecnico'}</span>
                    <small>{alvoAgenda(os, modoOficina)}</small>
                  </div>
                ))}
                {itens.length > 4 && <span className="agenda-more">+ {itens.length - 4} OS</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card agenda-dia-selecionado" style={{ marginTop: 16 }}>
        <div className="agenda-dia-head">
          <div>
            <h3>OS do dia</h3>
            <p>{tituloDataSelecionada}</p>
          </div>
          <span className="agenda-dia-count">{osDiaSelecionado.length} OS</span>
        </div>
        <div className="agenda-dia-lista">
          {osDiaSelecionado.map((os) => (
            <Link key={os.id} to={'/app/os/' + os.id} className="agenda-os agenda-os-detalhe" style={{ borderLeftColor: corStatus(os.status) }}>
              <div className="agenda-os-topo">
                <b>{hora(os)} {os.codigo}</b>
                <span className="badge" style={{ background: corStatus(os.status) + '15', color: corStatus(os.status), border: `1px solid ${corStatus(os.status)}33` }}>
                  {rotuloStatus(os.status)}
                </span>
              </div>
              <span>{servicoAgenda(os, modoOficina)} · {os.tecnico_nome || 'sem tecnico'}</span>
              <small>{alvoAgenda(os, modoOficina)}</small>
            </Link>
          ))}
          {!osDiaSelecionado.length && <p className="vazio">Nenhuma OS agendada para este dia nos filtros atuais.</p>}
        </div>
      </div>

      <div className="card agenda-sem-card" style={{ marginTop: 16 }}>
        <h3>Sem agendamento</h3>
        <div className="agenda-sem-lista">
          {semAgenda.map((os) => (
            <Link key={os.id} to={'/app/os/' + os.id} className="agenda-os" style={{ borderLeftColor: corStatus(os.status) }}>
              <b>{os.codigo}</b>
              <span>{rotuloStatus(os.status)} · {os.tecnico_nome || 'sem tecnico'}</span>
              <small>{alvoAgenda(os, modoOficina)}</small>
            </Link>
          ))}
          {!semAgenda.length && <p className="vazio">Nenhuma OS sem agendamento nos filtros atuais.</p>}
        </div>
      </div>

      {modalDiaAberto && createPortal((
        <>
          <div className="modal-backdrop fade show agenda-modal-backdrop" onClick={fecharModalDia} />
          <div className="modal fade show agenda-bs-modal" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="agendaModalDiaTitulo">
            <div className="modal-dialog modal-dialog-centered agenda-modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title" id="agendaModalDiaTitulo">OS do dia</h5>
                    <p>{tituloDataSelecionada}</p>
                  </div>
                  <button type="button" className="btn-close" aria-label="Close" onClick={fecharModalDia} />
                </div>
                <div className="modal-body">
                  <div className="agenda-dia-lista agenda-modal-lista">
                    {osDiaSelecionado.map((os) => (
                      <div key={os.id} className="agenda-os agenda-os-detalhe agenda-os-programavel" style={{ borderLeftColor: corStatus(os.status) }}>
                        <div className="agenda-os-topo">
                          <b>{hora(os)} {os.codigo}</b>
                          <span className="badge" style={{ background: corStatus(os.status) + '15', color: corStatus(os.status), border: `1px solid ${corStatus(os.status)}33` }}>
                            {rotuloStatus(os.status)}
                          </span>
                        </div>
                        <span>{servicoAgenda(os, modoOficina)} · {os.tecnico_nome || 'sem tecnico'}</span>
                        <small>{alvoAgenda(os, modoOficina)}</small>
                        <div className="agenda-os-acoes">
                          <Link to={'/app/os/' + os.id} className="btn sec sm">Abrir OS</Link>
                          {podeProgramarOs && (
                            <button type="button" className="btn prim sm" onClick={() => abrirProgramacao(os)}>Programar OS</button>
                          )}
                        </div>
                        {programandoOs?.id === os.id && (
                          <div className="agenda-programar-box">
                            <div className="agenda-programar-head">
                              <div>
                                <h6>Programar OS {programandoOs.codigo}</h6>
                                <p>{servicoAgenda(programandoOs, modoOficina)} - {alvoAgenda(programandoOs, modoOficina)}</p>
                              </div>
                              <button type="button" className="btn sec sm" onClick={() => setProgramandoOs(null)}>Cancelar</button>
                            </div>
                            <div className="agenda-programar-grid">
                              <div>
                                <label>Tipo</label>
                                <select value={programacaoModo} onChange={(e) => setProgramacaoModo(e.target.value as ProgramacaoModo)}>
                                  <option value="data">Uma data especifica</option>
                                  <option value="recorrente">Recorrente</option>
                                </select>
                              </div>
                              <div>
                                <label>{programacaoModo === 'data' ? 'Data' : 'Comecar em'}</label>
                                <input className="input" type="date" value={programacaoData} onChange={(e) => setProgramacaoData(e.target.value || hojeIso)} />
                              </div>
                              {programacaoModo === 'recorrente' && (
                                <div>
                                  <label>Frequencia</label>
                                  <select value={programacaoFrequencia} onChange={(e) => setProgramacaoFrequencia(e.target.value as ProgramacaoFrequencia)}>
                                    {FREQUENCIAS_PROGRAMACAO.map((f) => <option key={f.valor} value={f.valor}>{f.rotulo}</option>)}
                                  </select>
                                </div>
                              )}
                              {programacaoModo === 'recorrente' && programacaoFrequencia === 'semanal' && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <label>Dias da semana</label>
                                  <div className="agenda-programar-dias">
                                    {DIAS_COMPLETOS.map((rotulo, i) => {
                                      const marcado = programacaoDiasSemana.includes(i);
                                      return (
                                        <label key={i} className={'agenda-dia-chip' + (marcado ? ' on' : '')} title={rotulo}>
                                          <input
                                            type="checkbox"
                                            checked={marcado}
                                            onChange={() => setProgramacaoDiasSemana((atual) => (
                                              marcado ? atual.filter((d) => d !== i) : [...atual, i].sort()
                                            ))}
                                          />
                                          {DIAS[i]}
                                        </label>
                                      );
                                    })}
                                    <button
                                      type="button"
                                      className="lp-link"
                                      style={{ fontSize: 12 }}
                                      onClick={() => setProgramacaoDiasSemana((atual) => (atual.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6]))}
                                    >
                                      {programacaoDiasSemana.length === 7 ? 'Limpar' : 'Marcar todos'}
                                    </button>
                                  </div>
                                </div>
                              )}
                              {programacaoModo === 'recorrente' && (
                                <div>
                                  <label>Periodo</label>
                                  <select value={programacaoPeriodoMeses} onChange={(e) => setProgramacaoPeriodoMeses(e.target.value)}>
                                    {PERIODOS_PROGRAMACAO.map((m) => (
                                      <option key={m} value={m}>{m} {m === 1 ? 'mes' : 'meses'}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              <div>
                                <label>Hora</label>
                                <input className="input" type="time" value={programacaoHora} onChange={(e) => setProgramacaoHora(e.target.value)} />
                              </div>
                            </div>
                            {programacaoErro && <div className="msg-erro agenda-programar-msg">{programacaoErro}</div>}
                            {programacaoMsg && <div className="msg-ok agenda-programar-msg">{programacaoMsg}</div>}
                            <div className="agenda-programar-actions">
                              <button type="button" className="btn prim sm" onClick={salvarProgramacao} disabled={programacaoSalvando}>
                                {programacaoSalvando ? 'Programando...' : 'Gerar programacao'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {!osDiaSelecionado.length && <p className="vazio">Nenhuma OS agendada para este dia nos filtros atuais.</p>}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn sec sm" onClick={fecharModalDia}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      ), document.body)}

      {false && modalDiaAberto && (
        <div className="modal-bg agenda-modal-bg" onClick={() => setModalDiaAberto(false)}>
          <div className="modal agenda-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agenda-modal-head">
              <div>
                <h3>OS do dia</h3>
                <p>{tituloDataSelecionada}</p>
              </div>
              <button type="button" className="btn sec sm" onClick={() => setModalDiaAberto(false)}>Fechar</button>
            </div>
            <div className="agenda-dia-lista agenda-modal-lista">
                    {osDiaSelecionado.map((os) => (
                      <div key={os.id} className="agenda-os agenda-os-detalhe agenda-os-programavel" style={{ borderLeftColor: corStatus(os.status) }}>
                        <div className="agenda-os-topo">
                          <b>{hora(os)} {os.codigo}</b>
                          <span className="badge" style={{ background: corStatus(os.status) + '15', color: corStatus(os.status), border: `1px solid ${corStatus(os.status)}33` }}>
                            {rotuloStatus(os.status)}
                          </span>
                        </div>
                        <span>{servicoAgenda(os, modoOficina)} Â· {os.tecnico_nome || 'sem tecnico'}</span>
                        <small>{alvoAgenda(os, modoOficina)}</small>
                        <div className="agenda-os-acoes">
                          <Link to={'/app/os/' + os.id} className="btn sec sm">Abrir OS</Link>
                          {podeProgramarOs && (
                            <button type="button" className="btn prim sm" onClick={() => abrirProgramacao(os)}>Programar OS</button>
                          )}
                        </div>
                      </div>
                    ))}
              {!osDiaSelecionado.length && <p className="vazio">Nenhuma OS agendada para este dia nos filtros atuais.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
